import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
  InputAdornment,
} from '@mui/material'
import Grid from '@mui/material/GridLegacy'
import AddIcon from '@mui/icons-material/Add'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
  ICreateTestUserPayload,
  IKycProfile,
  ITestUserTemplate,
  TEST_USER_TEMPLATES,
} from '@/api/services/DeveloperService'
import {
  sectionTitleSx,
  subtleLabelSx,
  primaryButtonSx,
  outlinedButtonSx,
  checkboxSx,
  compactFormSx,
} from '../styles'
import { KycProfileSelector } from './KycProfileSelector'
import { hasMastercardAlias, withMastercardAlias } from './mastercardAlias'

interface Props {
  form: ICreateTestUserPayload
  isPending: boolean
  isError: boolean
  onSubmit: (e: React.FormEvent) => void
  onChange: (field: keyof ICreateTestUserPayload, value: string | boolean) => void
  onApplyTemplate: (template: ITestUserTemplate) => void
  onAutoFill: () => void
  onSelectKycProfile: (profile: IKycProfile | null) => void
}

export const CreateUserForm: React.FC<Props> = ({
  form,
  isPending,
  isError,
  onSubmit,
  onChange,
  onApplyTemplate,
  onAutoFill,
  onSelectKycProfile,
}) => {
  const [showPassword, setShowPassword] = useState(false)
  const [touched, setTouched] = useState<
    Record<'firstName' | 'lastName' | 'email' | 'mobile' | 'password', boolean>
  >({
    firstName: false,
    lastName: false,
    email: false,
    mobile: false,
    password: false,
  })
  const markTouched = (field: keyof typeof touched) =>
    setTouched(prev => ({ ...prev, [field]: true }))

  // Require exactly 9 digits after +61 (Australian mobile: +614XXXXXXXX)
  const mobileValid = /^\+61\d{9}$/.test(form.mobile)
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
  // Generating a token right after creation needs a known password — the backend's default
  // test password isn't available to the frontend, so require one to be set explicitly.
  const passwordValid = !form.generateToken || !!form.password
  const isFormValid = form.firstName && form.lastName && emailValid && mobileValid && passwordValid

  const firstNameError = touched.firstName && !form.firstName
  const lastNameError = touched.lastName && !form.lastName
  const emailError = touched.email && !emailValid
  const mobileError = touched.mobile && !mobileValid
  const passwordError = touched.password && !passwordValid

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, borderColor: '#e0d7e4' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mb: 1.5 }}>
          Create test user
        </Typography>

        {/* Scenario templates */}
        <Typography variant="caption" sx={{ ...subtleLabelSx, mb: 0.75, display: 'block' }}>
          Quick scenario templates - Note: These are ideas only and is not functional
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {TEST_USER_TEMPLATES.map(t => {
            const isSelected = form.scenarioTag === t.preset.scenarioTag
            return (
              <Tooltip key={t.id} title={t.description} arrow>
                <Chip
                  label={t.label}
                  size="small"
                  onClick={() => onApplyTemplate(t)}
                  disabled={true}
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 500,
                    backgroundColor: isSelected ? '#fa624b' : 'transparent',
                    color: isSelected ? '#2c063f' : '#4b2b5c',
                    borderColor: isSelected ? '#fa624b' : '#cac0cf',
                    border: '1px solid',
                    '&:hover': { backgroundColor: isSelected ? '#e5432e' : '#f5f2f7' },
                  }}
                />
              </Tooltip>
            )
          })}
        </Box>

        <Divider sx={{ mb: 1.5, borderColor: '#e0d7e4' }} />

        {/* KYC profile */}
        <Box sx={{ mb: 1.5 }}>
          <KycProfileSelector selectedProfileId={form.kycProfileId} onSelect={onSelectKycProfile} />
        </Box>

        <Divider sx={{ mb: 2, borderColor: '#e0d7e4' }} />

        <Box component="form" onSubmit={onSubmit} sx={compactFormSx}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="First name"
                value={form.firstName}
                onChange={e => onChange('firstName', e.target.value)}
                onBlur={() => markTouched('firstName')}
                error={!!firstNameError}
                helperText={firstNameError ? 'Required' : ' '}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Last name"
                value={form.lastName}
                onChange={e => onChange('lastName', e.target.value)}
                onBlur={() => markTouched('lastName')}
                error={!!lastNameError}
                helperText={lastNameError ? 'Required' : ' '}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Date of birth"
                value={form.dateOfBirth}
                onChange={e => onChange('dateOfBirth', e.target.value)}
                fullWidth
                size="small"
                type="date"
                helperText=" "
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                label="Email"
                value={form.email}
                onChange={e => onChange('email', e.target.value)}
                onBlur={() => markTouched('email')}
                error={!!emailError}
                helperText={emailError ? 'Enter a valid email address' : ' '}
                fullWidth
                size="small"
                type="email"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Tooltip title="Auto-generate test values">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onAutoFill}
                  fullWidth
                  sx={{ ...outlinedButtonSx, whiteSpace: 'nowrap', height: 40 }}
                >
                  Auto-fill
                </Button>
              </Tooltip>
            </Grid>

            <Grid item xs={5} sm={2}>
              {/* Country code — hardcoded to AU for now; extend to a Select when multi-country is needed */}
              <TextField
                label="Code"
                value="+61"
                size="small"
                disabled
                helperText=" "
                fullWidth
                sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#4b2b5c' } }}
              />
            </Grid>
            <Grid item xs={7} sm={4}>
              <TextField
                label="Mobile"
                value={form.mobile.startsWith('+61') ? form.mobile.slice(3) : form.mobile}
                onChange={e => {
                  const digits = e.target.value
                    .replace(/\D/g, '')
                    .replace(/^61/, '')
                    .replace(/^0/, '')
                  onChange('mobile', digits ? `+61${digits}` : '')
                }}
                onBlur={() => markTouched('mobile')}
                error={!!mobileError}
                helperText={mobileError ? '9 digits, e.g. 400000000' : ' '}
                placeholder="400000000"
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                value={form.password}
                onChange={e => {
                  onChange('password', e.target.value)
                  // Can't generate a token without a password to hand the backend — uncheck if it's cleared
                  if (!e.target.value && form.generateToken) onChange('generateToken', false)
                }}
                onBlur={() => markTouched('password')}
                error={!!passwordError}
                fullWidth
                size="small"
                type={showPassword ? 'text' : 'password'}
                helperText={
                  passwordError
                    ? 'Required to generate a token'
                    : 'Optional — default test password if blank'
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(prev => !prev)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Options */}
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ ...subtleLabelSx, display: 'block', mb: 0.25 }}>
                Options Note: bank connection, loan application and active loan are ideas only and
                are not functional
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  columnGap: 2,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                {(
                  [
                    { field: 'hasBankConnection', label: 'Bank connection' },
                    { field: 'hasLoanApplication', label: 'Loan application' },
                    { field: 'hasLoan', label: 'Active loan' },
                  ] as const
                ).map(({ field, label }) => (
                  <FormControlLabel
                    key={field}
                    control={
                      <Checkbox
                        size="small"
                        checked={form[field] as boolean}
                        onChange={e => onChange(field, e.target.checked)}
                        disabled={true}
                        sx={checkboxSx}
                      />
                    }
                    label={
                      <Typography variant="body2" color="#4b2b5c">
                        {label}
                      </Typography>
                    }
                  />
                ))}
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={hasMastercardAlias(form.email)}
                      disabled={!form.email.includes('@')}
                      onChange={e =>
                        onChange('email', withMastercardAlias(form.email, e.target.checked))
                      }
                      sx={checkboxSx}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      color={form.email.includes('@') ? '#4b2b5c' : '#a08fa8'}
                    >
                      Mastercard ready user
                    </Typography>
                  }
                />
                <Tooltip
                  title={form.password ? '' : 'Enter a password above first'}
                  disableHoverListener={!!form.password}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={form.generateToken}
                        disabled={!form.password}
                        onChange={e => onChange('generateToken', e.target.checked)}
                        sx={checkboxSx}
                      />
                    }
                    label={
                      <Typography variant="body2" color={form.password ? '#4b2b5c' : '#a08fa8'}>
                        Generate auth token
                      </Typography>
                    }
                  />
                </Tooltip>
              </Box>
              {hasMastercardAlias(form.email) && (
                <Typography variant="caption" sx={{ ...subtleLabelSx, display: 'block', mt: -0.5 }}>
                  Tagged in the email alias, which is what makes the user cash out as a Mastercard
                  customer
                </Typography>
              )}
              {form.generateToken && (
                <Typography variant="caption" sx={{ ...subtleLabelSx, display: 'block', mt: -0.5 }}>
                  The token will appear in the Test users table below once ready
                </Typography>
              )}
            </Grid>

            {isError && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ py: 0.5 }}>
                  Failed to create test user. Check the API logs.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="medium"
                  disabled={!isFormValid || isPending}
                  startIcon={
                    isPending ? <CircularProgress size={16} color="inherit" /> : <AddIcon />
                  }
                  sx={primaryButtonSx}
                >
                  {isPending ? 'Creating…' : 'Create test user'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  )
}
